import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { CircularProgress } from '@mui/material'
import { useGetBusinessIfExists } from '@marketing-milk/frontend'
import { useSprintPacket } from 'hooks/api/useSprintPackets'
import { CommentThread } from 'components/comments/CommentThread'
import { useCommentAnchors, readCommentDeepLink } from './useCommentAnchors'
import { SECTIONS, SectionDef } from './sections'
import { dark, type } from '../tokens'
import { PresentCover } from './present/PresentCover'
import { PresentProgress } from './present/PresentProgress'
import { PresentPerformance } from './present/sections/PresentPerformance'
import { PresentNarrative } from './present/sections/PresentNarrative'
import { PresentClientSentiment } from './present/sections/PresentClientSentiment'
import { PresentAudienceSection } from './present/sections/PresentAudienceSection'
import { PresentCreativeSection } from './present/sections/PresentCreativeSection'
import { PresentNavLockContext } from './present/PresentNavLock'
import { creativeWizardUrl } from '../../creatives/creativeUrl'

interface Props {
  businessID: number
  packetID: number
}

/** Compile-time exhaustiveness guard for the section-kind switch. */
const assertNever = (x: never): never => {
  throw new Error(`Unhandled section kind: ${x}`)
}

type Page = { kind: 'cover' } | { kind: 'section'; section: SectionDef }

const PAGES: Page[] = [
  { kind: 'cover' },
  ...SECTIONS.map(s => ({ kind: 'section' as const, section: s })),
]

const PAGE_LABELS = ['Cover', ...SECTIONS.map(s => s.label)]

export function PacketPresentBody({ businessID, packetID }: Props) {
  const { packet, isLoading, isError, refetch } = useSprintPacket(packetID)
  const business = useGetBusinessIfExists()
  const history = useHistory()
  const { anchor, open, close, setAnchor, setOpen, setRoot } = useCommentAnchors()
  const [pageIndex, setPageIndex] = useState(0)
  const [navLocked, setNavLocked] = useState(false)
  const handledDeepLinkRef = useRef(false)

  // Slack comment deep-link (`?commentId=&anchorType=&anchorId=`): jump the deck
  // to the matching slide, then open the thread. anchorId is `<packetID>:<key>`;
  // for section/narrative anchors <key> is a SectionKey, for performance-metric
  // it's a metric key, so fall back to the performance-review slide. The ref latch
  // keeps this run-once so a packet refetch can't yank the viewer back mid-deck.
  useEffect(() => {
    if (!packet || handledDeepLinkRef.current) return
    const link = readCommentDeepLink()
    if (!link) return
    handledDeepLinkRef.current = true
    const key = link.anchorId.slice(link.anchorId.indexOf(':') + 1)
    let idx = PAGES.findIndex(p => p.kind === 'section' && p.section.key === key)
    if (idx < 0 && link.anchorType === 'performance-metric') {
      idx = PAGES.findIndex(p => p.kind === 'section' && p.section.key === 'performance-review')
    }
    if (idx >= 0) setPageIndex(idx)
    setAnchor({ type: link.anchorType, id: link.anchorId })
    setOpen(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packet])

  const exitToEditor = useMemo(
    () => (index: number) => {
      const page = PAGES[index]
      const hash = page.kind === 'section' ? `#${page.section.key}` : ''
      history.push(`/${businessID}/sprint-packets/${packetID}/edit${hash}`)
    },
    [history, businessID, packetID]
  )

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Don't hijack keys while the AE is typing in the live comment drawer or an
      // open in-slide modal (e.g. creative approval) — arrow keys must move the
      // text cursor and Esc must close the modal, not discard work or exit the deck.
      if (open || navLocked) return
      const t = e.target as HTMLElement | null
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return
      if (e.key === 'ArrowRight') setPageIndex(i => Math.min(i + 1, PAGES.length - 1))
      else if (e.key === 'ArrowLeft') setPageIndex(i => Math.max(i - 1, 0))
      else if (e.key === 'Escape') exitToEditor(pageIndex)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [pageIndex, exitToEditor, open, navLocked])

  const baseStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: dark.bg,
    color: dark.text,
    fontFamily: type.family.sans,
  }

  if (isLoading) {
    return (
      <div
        style={{ ...baseStyle, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <CircularProgress size={28} sx={{ color: dark.accent }} />
      </div>
    )
  }

  if (isError || !packet) {
    return (
      <div
        style={{ ...baseStyle, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <h2
            style={{
              margin: 0,
              fontFamily: type.family.serif,
              fontSize: type.size.h1,
              fontWeight: type.weight.regular,
            }}
          >
            Couldn't load this sprint packet
          </h2>
          <button
            type="button"
            onClick={() => refetch()}
            style={{
              alignSelf: 'center',
              padding: '8px 16px',
              borderRadius: 6,
              border: `1px solid ${dark.hairline}`,
              background: dark.surface,
              color: dark.text,
              fontFamily: type.family.mono,
              fontSize: type.size.eyebrow,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              cursor: 'pointer',
            }}
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  const renderSection = (s: SectionDef) => {
    switch (s.kind) {
      case 'performance':
        return <PresentPerformance palette={dark} packet={packet} />
      case 'narrative':
        return (
          <PresentNarrative
            palette={dark}
            sectionKey={
              s.key as Exclude<
                typeof s.key,
                'performance-review' | 'client-sentiment' | 'audience-framework' | 'creative'
              >
            }
            label={s.label}
            packetID={packet.id}
            narrative={packet.narrative ?? []}
          />
        )
      case 'client-sentiment':
        return (
          <PresentClientSentiment
            palette={dark}
            packetID={packet.id}
            sentiment={packet.clientSentiment ?? null}
          />
        )
      case 'audience':
        return (
          <PresentAudienceSection
            palette={dark}
            packetID={packet.id}
            audiences={packet.audiences}
            audienceSelection={packet.audienceSelection ?? null}
          />
        )
      case 'creative':
        return (
          <PresentCreativeSection
            palette={dark}
            businessID={businessID}
            packetID={packet.id}
            sprintID={packet.sprintID}
          />
        )
      default:
        // Exhaustive over SectionKind today; a new kind fails the type-check here.
        return assertNever(s.kind)
    }
  }

  return (
    <PresentNavLockContext.Provider value={setNavLocked}>
      <style>{`
        .present-slide-scroll::-webkit-scrollbar { width: 8px; }
        .present-slide-scroll::-webkit-scrollbar-track { background: transparent; }
        .present-slide-scroll::-webkit-scrollbar-thumb { background: ${dark.hairline}; border-radius: 4px; }
        .present-slide-scroll::-webkit-scrollbar-thumb:hover { background: ${dark.textFaint}; }
      `}</style>
      <div ref={setRoot} style={baseStyle}>
        <button
          type="button"
          onClick={() => exitToEditor(pageIndex)}
          style={{
            position: 'absolute',
            top: 20,
            right: 24,
            zIndex: 2,
            padding: '6px 12px',
            borderRadius: 6,
            border: `1px solid ${dark.hairline}`,
            background: dark.surface,
            color: dark.textMuted,
            fontFamily: type.family.mono,
            fontSize: type.size.eyebrow,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            cursor: 'pointer',
          }}
        >
          Esc · Editor
        </button>

        {PAGES.map((page, i) => {
          const isActive = i === pageIndex
          return (
            <div
              key={i}
              aria-hidden={!isActive}
              style={{
                position: 'absolute',
                inset: 0,
                opacity: isActive ? 1 : 0,
                pointerEvents: isActive ? 'auto' : 'none',
                // `visibility: hidden` pulls inactive slides out of the tab order and
                // the accessibility tree (their comment triggers would otherwise stay
                // focusable). Delay the flip on the way out so the 200ms fade still plays.
                visibility: isActive ? 'visible' : 'hidden',
                transition: isActive
                  ? 'opacity 200ms ease'
                  : 'opacity 200ms ease, visibility 0s linear 200ms',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <div
                className="present-slide-scroll"
                style={{
                  width: '100%',
                  maxWidth: 1024,
                  height: '100%',
                  overflowY: 'auto',
                  padding: '64px 48px 88px',
                  scrollbarWidth: 'thin',
                  scrollbarColor: `${dark.hairline} transparent`,
                }}
              >
                {page.kind === 'cover' ? (
                  <PresentCover
                    palette={dark}
                    packet={packet}
                    businessName={business?.businessName}
                    phase={business?.phase}
                  />
                ) : (
                  renderSection(page.section)
                )}
              </div>
            </div>
          )
        })}

        <PresentProgress
          palette={dark}
          count={PAGES.length}
          current={pageIndex}
          onJump={setPageIndex}
          labels={PAGE_LABELS}
        />
      </div>

      <CommentThread
        open={open}
        onClose={close}
        businessID={businessID}
        anchor={anchor}
        notificationContext={{
          header: `Sprint Packet — ${packet.sprint?.name ?? `#${packet.sprintID}`}`,
          pageUrlBase: window.location.href.split('?')[0],
          pageUrlBaseForAnchor: a =>
            a.type === 'creative' ? creativeWizardUrl(businessID, a.id) : undefined,
        }}
      />
    </PresentNavLockContext.Provider>
  )
}
